'use client';

/*
  AuthRequiredCheck

  Wrapper component that checks for authentication errors, and redirects to the signin page
  if an error is detected.
*/

import { useEffect } from 'react';
import * as Sentry from '@sentry/nextjs';
import { signOut } from 'next-auth/react';
import { env } from 'next-runtime-env';

import { usePathname } from '@/config';
import { isPublicPage } from '@/utils/publicPages';

const AUTH_ERROR_EVENT = 'auth-error';

// Module-level flag to prevent multiple concurrent 401s from triggering
// multiple signOut calls / redirects.
let _isRedirecting = false;

async function handleAuthError() {
  if (_isRedirecting) return;
  _isRedirecting = true;

  try {
    Sentry.captureMessage(
      'AuthRequiredCheck: detected auth error, signing out..',
    );

    await signOut({ redirect: false });

    const signinUrl = `${globalThis.location.origin}/signin?error=SessionExpired`;
    const moiLogoutUrl = new URL(
      '/ohub/authentication/logout',
      env('NEXT_PUBLIC_MOI_SERVICE_URL'),
    );
    moiLogoutUrl.searchParams.set('redirect_uri', signinUrl);
    globalThis.location.href = moiLogoutUrl.toString();
  } catch (err) {
    _isRedirecting = false;
    Sentry.captureException(err);
  }
}

export function AuthRequiredCheck({
  children,
}: {
  readonly children?: React.ReactNode;
}) {
  const pathname = usePathname();

  useEffect(() => {
    if (isPublicPage(pathname)) return;

    function onAuthError() {
      void handleAuthError();
    }

    globalThis.addEventListener(AUTH_ERROR_EVENT, onAuthError);
    return () => globalThis.removeEventListener(AUTH_ERROR_EVENT, onAuthError);
  }, [pathname]);

  return <>{children}</>;
}

export function dispatchAuthError() {
  if (typeof window === 'undefined' || _isRedirecting) return;
  globalThis.dispatchEvent(new CustomEvent(AUTH_ERROR_EVENT));
}
